import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form as AntForm,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
  inventoryTypeReclassifyRuleService,
  type Rule,
  type RuleInput,
} from '@services/admin/inventoryTypeReclassifyRuleService';
import { inventoryTypeService } from '@services/admin/inventoryTypeService';

type DraftTier = {
  id?: string;
  original_expiration_date_threshold: number;
  trigger_mechanism: 'by_days' | 'by_ratio';
  days_before_expiration: number | null;
  expiration_ratio: number | null;
};

const blankTier = (threshold: number): DraftTier => ({
  original_expiration_date_threshold: threshold,
  trigger_mechanism: 'by_days',
  days_before_expiration: 7,
  expiration_ratio: null,
});

type ServerError = {
  success: false;
  message?: string;
  interpolations?: { cycle?: string[] } & Record<string, unknown>;
};

const i18nBase = 'admin.pages.inventory_type_reclassify_rules.form';

/**
 * 採與 sales_channel_rule 一致的下限語意：
 * `original_expiration_date_threshold` 為適用 product.validity_days 下限（含）。
 * 同 rule 內以 threshold 升冪排列後，每階覆蓋 `[threshold, next.threshold)`；
 * 最高一階開上無界。最低 threshold（通常為 0）即承擔小區段 catch-all。
 *
 * Mechanism switcher (by_days / by_ratio) 切換哪一個門檻欄位可填；
 * payload 永遠送雙欄位（未用那邊強制 null），讓 backend
 * `mechanism_threshold_consistency` CHECK 一致地驗證。
 *
 * Cycle errors (RECLASSIFY_RULE_CYCLE_DETECTED) 由 backend 帶
 * interpolations.cycle，前端組成箭頭字串顯示。
 */
export default function AdminInventoryTypeReclassifyRuleForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id && id !== 'new';

  const [name, setName] = useState('');
  const [sourceTypeId, setSourceTypeId] = useState<string | null>(null);
  const [targetTypeId, setTargetTypeId] = useState<string | null>(null);
  const [tiers, setTiers] = useState<DraftTier[]>([blankTier(0)]);
  const [typeOptions, setTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    inventoryTypeService
      .fetchList()
      .then((res: { success?: boolean; data?: Array<{ id: string; name: string }> }) => {
        if (!alive) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setTypeOptions(list.map((opt) => ({ value: opt.id, label: opt.name })));
      })
      .catch((err) => console.error('Failed to load inventory types', err));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    inventoryTypeReclassifyRuleService.show(id!).then((res) => {
      if (!alive || !res.success) return;
      const r: Rule = res.data;
      setName(r.name);
      setSourceTypeId(r.source_inventory_type_id);
      setTargetTypeId(r.target_inventory_type_id);
      setTiers(
        r.tiers.map((tier) => ({
          id: tier.id,
          original_expiration_date_threshold: tier.original_expiration_date_threshold,
          trigger_mechanism: tier.trigger_mechanism,
          days_before_expiration: tier.days_before_expiration,
          expiration_ratio: tier.expiration_ratio,
        })),
      );
    });
    return () => {
      alive = false;
    };
  }, [id, isEdit]);

  // 階梯依 threshold 升冪呈現，方便視覺從小到大；下一階 threshold 用來算上界顯示
  const sortedTierIndexes = useMemo(() => {
    return tiers
      .map((tier, idx) => ({ tier, idx }))
      .sort(
        (a, b) =>
          a.tier.original_expiration_date_threshold -
          b.tier.original_expiration_date_threshold,
      );
  }, [tiers]);

  const duplicateThresholds = useMemo(() => {
    const seen = new Map<number, number>();
    tiers.forEach((tier) => {
      const v = tier.original_expiration_date_threshold;
      seen.set(v, (seen.get(v) ?? 0) + 1);
    });
    return new Set(
      [...seen.entries()].filter(([, count]) => count > 1).map(([v]) => v),
    );
  }, [tiers]);

  const nextThresholdFor = (idx: number): number | null => {
    const sortedPos = sortedTierIndexes.findIndex((s) => s.idx === idx);
    if (sortedPos < 0) return null;
    const next = sortedTierIndexes[sortedPos + 1];
    return next ? next.tier.original_expiration_date_threshold : null;
  };

  const formatRange = (threshold: number, next: number | null): string => {
    if (next == null) {
      return t(`${i18nBase}.condition_range_open_ended`, { from: threshold });
    }
    return t(`${i18nBase}.condition_range_from_to`, { from: threshold, to: next - 1 });
  };

  const updateTier = (idx: number, patch: Partial<DraftTier>) => {
    setTiers((ts) => ts.map((tier, i) => (i === idx ? { ...tier, ...patch } : tier)));
  };

  const addTier = () => {
    setTiers((ts) => {
      const maxThreshold = ts.length
        ? Math.max(...ts.map((tier) => tier.original_expiration_date_threshold))
        : -1;
      return [...ts, blankTier(maxThreshold + 1)];
    });
  };

  const removeTier = (idx: number) => {
    setTiers((ts) => ts.filter((_, i) => i !== idx));
  };

  const onSave = async () => {
    setErrorBanner(null);

    if (!sourceTypeId || !targetTypeId) {
      setErrorBanner(
        `${t(`${i18nBase}.source_type`)} / ${t(`${i18nBase}.target_type`)}`,
      );
      return;
    }
    if (sourceTypeId === targetTypeId) {
      // backend 有 CHECK constraint 與 cycle detection 會擋，但純 DB error 沒對應的 AppErrors code，
      // 落到前端 banner 會變空字串。先在這邊擋掉，給使用者明確訊息。
      setErrorBanner(t(`${i18nBase}.source_target_must_differ`));
      return;
    }
    if (tiers.length === 0) {
      setErrorBanner(t('errors.RECLASSIFY_RULE_REQUIRES_TIER'));
      return;
    }
    if (duplicateThresholds.size > 0) {
      setErrorBanner(t(`${i18nBase}.duplicate_threshold`));
      return;
    }

    const payload: RuleInput = {
      name,
      source_inventory_type_id: sourceTypeId,
      target_inventory_type_id: targetTypeId,
      tiers_attributes: tiers.map((tier) => ({
        id: tier.id,
        original_expiration_date_threshold: tier.original_expiration_date_threshold,
        trigger_mechanism: tier.trigger_mechanism,
        days_before_expiration:
          tier.trigger_mechanism === 'by_days' ? tier.days_before_expiration : null,
        expiration_ratio:
          tier.trigger_mechanism === 'by_ratio' ? tier.expiration_ratio : null,
      })),
    };

    setSaving(true);
    try {
      const result = isEdit
        ? await inventoryTypeReclassifyRuleService.update(id!, payload)
        : await inventoryTypeReclassifyRuleService.create(payload);

      if (result.success) {
        navigate('/inventory_type_reclassify_rules');
        return;
      }

      const err = result as unknown as ServerError;
      const code = err.message;
      if (code === 'RECLASSIFY_RULE_CYCLE_DETECTED') {
        // backend 回的是 inventory_type UUID 陣列；查 typeOptions 換成顯示名稱，
        // 找不到時 fallback 為 UUID 以免訊息空掉（罕見：跨表單載入 race）。
        const nameById = new Map(typeOptions.map((opt) => [opt.value, opt.label]));
        const cycle = (err.interpolations?.cycle ?? [])
          .map((typeId) => nameById.get(typeId) ?? typeId)
          .join(' → ');
        setErrorBanner(t(`${i18nBase}.cycle_error`, { cycle }));
      } else if (code) {
        setErrorBanner(t(`errors.${code}`, { defaultValue: code }));
      } else {
        setErrorBanner(t('common.error.unknown', { defaultValue: 'Unknown error' }));
      }
    } catch (e) {
      setErrorBanner(String((e as Error).message ?? e));
    } finally {
      setSaving(false);
    }
  };

  const tierColumns = [
    {
      title: t(`${i18nBase}.condition_action`),
      key: 'action',
      width: 64,
      align: 'center' as const,
      render: (_: unknown, _row: DraftTier, idx: number) => (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => removeTier(idx)}
          aria-label={t(`${i18nBase}.remove_condition`)}
        />
      ),
    },
    {
      title: (
        <Space>
          {t(`${i18nBase}.condition_threshold`)}
          <Tooltip title={t(`${i18nBase}.condition_section_hint`)}>
            <QuestionCircleOutlined />
          </Tooltip>
        </Space>
      ),
      key: 'threshold',
      width: 320,
      render: (_: unknown, row: DraftTier, idx: number) => {
        const next = nextThresholdFor(idx);
        const rangeText = formatRange(row.original_expiration_date_threshold, next);
        const isDuplicate = duplicateThresholds.has(row.original_expiration_date_threshold);
        return (
          <Space direction="vertical" size={6}>
            <InputNumber
              min={0}
              value={row.original_expiration_date_threshold}
              onChange={(v) =>
                updateTier(idx, {
                  original_expiration_date_threshold: v == null ? 0 : Number(v),
                })
              }
              addonAfter={t(`${i18nBase}.condition_threshold_unit`)}
              status={isDuplicate ? 'error' : undefined}
            />
            <Tag color={isDuplicate ? 'error' : 'processing'}>
              {t(`${i18nBase}.condition_range_label`)}：{rangeText}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: t(`${i18nBase}.trigger_header`),
      key: 'condition',
      render: (_: unknown, row: DraftTier, idx: number) => (
        <Space direction="vertical" size={6}>
          <Segmented
            value={row.trigger_mechanism}
            onChange={(v) =>
              updateTier(idx, { trigger_mechanism: v as 'by_days' | 'by_ratio' })
            }
            options={[
              { label: t(`${i18nBase}.mechanism_by_days`), value: 'by_days' },
              { label: t(`${i18nBase}.mechanism_by_ratio`), value: 'by_ratio' },
            ]}
          />
          {row.trigger_mechanism === 'by_days' ? (
            <InputNumber
              min={1}
              value={row.days_before_expiration ?? undefined}
              onChange={(v) =>
                updateTier(idx, {
                  days_before_expiration: v == null ? null : Number(v),
                })
              }
              addonBefore={t(`${i18nBase}.condition_threshold_days`)}
            />
          ) : (
            <InputNumber
              min={0}
              max={100}
              value={row.expiration_ratio ?? undefined}
              onChange={(v) =>
                updateTier(idx, {
                  expiration_ratio: v == null ? null : Number(v),
                })
              }
              addonBefore={t(`${i18nBase}.condition_threshold_ratio_pct`)}
              addonAfter="%"
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={t(isEdit ? `${i18nBase}.title_edit` : `${i18nBase}.title_new`)}
    >
      {errorBanner && (
        <Alert
          type="error"
          message={errorBanner}
          closable
          onClose={() => setErrorBanner(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <AntForm layout="vertical">
        <Row gutter={16}>
          <Col span={24}>
            <AntForm.Item label={t(`${i18nBase}.name`)} required>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </AntForm.Item>
          </Col>
          <Col span={12}>
            <AntForm.Item label={t(`${i18nBase}.source_type`)} required>
              <Select
                value={sourceTypeId}
                onChange={setSourceTypeId}
                options={typeOptions.map((opt) => ({
                  ...opt,
                  disabled: opt.value === targetTypeId,
                }))}
              />
            </AntForm.Item>
          </Col>
          <Col span={12}>
            <AntForm.Item label={t(`${i18nBase}.target_type`)} required>
              <Select
                value={targetTypeId}
                onChange={setTargetTypeId}
                options={typeOptions.map((opt) => ({
                  ...opt,
                  disabled: opt.value === sourceTypeId,
                }))}
              />
            </AntForm.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>
          <Space>
            {t(`${i18nBase}.condition_section`)}
            <Tooltip title={t(`${i18nBase}.condition_section_hint`)}>
              <QuestionCircleOutlined />
            </Tooltip>
          </Space>
        </Divider>

        <Table<DraftTier>
          rowKey={(_row, idx) => `tier-${idx}`}
          dataSource={tiers}
          columns={tierColumns}
          pagination={false}
          bordered
          size="middle"
          footer={() => (
            <Button
              type="dashed"
              onClick={addTier}
              block
              icon={<PlusOutlined />}
            >
              {t(`${i18nBase}.add_condition`)}
            </Button>
          )}
        />

        <Divider />
        <Row justify="end">
          <Space>
            <Button onClick={() => navigate('/inventory_type_reclassify_rules')}>
              {t(`${i18nBase}.cancel`)}
            </Button>
            <Button type="primary" onClick={onSave} loading={saving}>
              {saving ? t(`${i18nBase}.saving`) : t(`${i18nBase}.save`)}
            </Button>
          </Space>
        </Row>
      </AntForm>
    </Card>
  );
}
